import React, { useState } from "react";
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import {
  ExitToApp as LogOut,
  Notifications as Bell,
  Settings,
  Person as User
} from '@mui/icons-material';

const NavbarContainer = styled('header')`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: var(--surface);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
  z-index: var(--z-navbar);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-lg);
`;

const NavbarBrand = styled('div')`
  display: flex;
  align-items: center;
`;

const BrandLogo = styled('div')`
  width: 40px;
  height: 40px;
  background: var(--accent-gradient);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--space-md);
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
`;

const BrandText = styled('div')`
  display: flex;
  flex-direction: column;
`;

const BrandTitle = styled('h1')`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const BrandSubtitle = styled('span')`
  font-size: 0.75rem;
  color: var(--text-tertiary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const NavbarActions = styled('div')`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const ActionButton = styled('button')`
  width: 44px;
  height: 44px;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  position: relative;

  &:hover {
    background: var(--surface-hover);
    color: var(--text-primary);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const NotificationBadge = styled('div')`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: var(--error);
  border-radius: 50%;
  border: 2px solid var(--surface);
`;

const UserMenu = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
`;

const UserButton = styled('button')`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--surface-hover);
  }
`;

const UserAvatar = styled('div')`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--accent-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserInfo = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled('span')`
  font-weight: 600;
  line-height: 1.2;
`;

const UserRole = styled('span')`
  font-size: 0.75rem;
  color: var(--text-tertiary);
  line-height: 1;
`;

const DropdownMenu = styled('div')`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: var(--space-sm);
  min-width: 200px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: 0 10px 25px var(--shadow);
  z-index: var(--z-modal);
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all var(--transition-fast);
`;

const DropdownItem = styled('button')`
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-md);
  transition: background-color var(--transition-fast);

  &:hover {
    background: var(--surface-hover);
  }

  &:first-child {
    border-radius: var(--radius-md) var(--radius-md) 0 0;
  }

  &:last-child {
    border-radius: 0 0 var(--radius-md) var(--radius-md);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

function Navbar() {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setShowUserMenu(false);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(prev => !prev);
  };

  const closeUserMenu = () => {
    setShowUserMenu(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        closeUserMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  return (
    <NavbarContainer>
      <NavbarBrand>
        <BrandLogo>🛒</BrandLogo>
        <BrandText>
          <BrandTitle>SmartGrocer</BrandTitle>
          <BrandSubtitle>Admin Dashboard</BrandSubtitle>
        </BrandText>
      </NavbarBrand>

      <NavbarActions>
        <ActionButton title="Notifications">
          <Bell />
          <NotificationBadge />
        </ActionButton>

        <ActionButton title="Settings">
          <Settings />
        </ActionButton>

        <UserMenu className="user-menu">
          <UserButton onClick={toggleUserMenu}>
            <UserAvatar>A</UserAvatar>
            <UserInfo>
              <UserName>Admin</UserName>
              <UserRole>Administrator</UserRole>
            </UserInfo>
          </UserButton>

          <DropdownMenu show={showUserMenu}>
            <DropdownItem onClick={() => setShowUserMenu(false)}>
              <User />
              Profile Settings
            </DropdownItem>
            <DropdownItem onClick={handleLogout}>
              <LogOut />
              Sign Out
            </DropdownItem>
          </DropdownMenu>
        </UserMenu>
      </NavbarActions>
    </NavbarContainer>
  );
}

export default Navbar;
